'use client';

import { Button } from '@mui/material';
import { Children, forwardRef, ReactNode } from 'react';

import { TranslatableButtonProps } from './translatableButton.props';

/**
 * Wraps string/number children in a <span> so browser translation extensions
 * (e.g. Google Translate) can reliably detect and replace the button label.
 *
 * The span is keyed on its content: when the text changes, React remounts the
 * whole span instead of mutating the text node in place. This dodges the
 * classic Google Translate crash where the extension wraps text nodes in
 * <font> tags, causing `NotFoundError: Failed to execute 'removeChild'` when
 * React later tries to update or remove the (now-relocated) text node.
 * See https://github.com/facebook/react/issues/11538
 *
 * React elements (icons, divs, already-wrapped spans) are left untouched,
 * so complex children still work — wrap nested text manually if needed
 * (and use a content-based key there too when the text can change).
 */
const wrapTextChildren = (children: ReactNode): ReactNode => {
  return Children.map(children, (child) => {
    if (typeof child === 'string' || typeof child === 'number') {
      return <span key={`tr-${String(child)}`}>{child}</span>;
    }
    return child;
  });
};

/**
 * Drop-in replacement for MUI Button that auto-wraps text children in <span>
 * for translation-extension support.
 *
 * @example
 * // Simple text — auto-wrapped
 * <TranslatableButton>Save</TranslatableButton>
 *
 * // With icon — text auto-wrapped, icon untouched
 * <TranslatableButton startIcon={<Icon />}>Save</TranslatableButton>
 *
 * // Complex children — wrap nested text manually
 * <TranslatableButton>
 *   <div className='flex'>
 *     <Spinner />
 *     <span>Loading...</span>
 *   </div>
 * </TranslatableButton>
 */
const TranslatableButton = forwardRef<HTMLButtonElement, TranslatableButtonProps>(
  ({ children, ...props }, ref) => {
    return (
      <Button {...props} ref={ref}>
        {wrapTextChildren(children)}
      </Button>
    );
  }
);

TranslatableButton.displayName = 'TranslatableButton';

export default TranslatableButton;
export { wrapTextChildren };
